import { useQuery, useMutation, useApolloClient } from '@apollo/client/react';
import React, { useEffect, useMemo, useState } from 'react';
import { v5 as uuidv5 } from 'uuid';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Alert,
    Chip,
    Paper,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppSelector } from '../../hooks/redux';
import GeneralSelector from '../picker/GeneralSelector';
import ModalPickUpBid from './ModalPickUpBid';
import ModalDropOff, { DropOffData } from './ModalDropOff';
import { TransactorCompaniesV2 } from '../../queries/transactor';
import { NgpamCreateContract, NamCreateContract, NamSetDropoff, NgpamSetDropoff, getContractAgent } from '../../queries/contract';
import { GET_DROPOFF_PHOTO_URL } from '../../queries/utils';
import { PriceModel } from '../../models/price';
import { ContractNAM } from '../../models/contract/ContractNAM';
import { Organisation } from '../../models/organisation/OrganisationModelV2';
import { extractStoragePath, isNullOrEmpty, isNullOrUndefined } from '../../utils/helper';

// Temporary type definitions until proper models are created
interface GroupModel {
    group_id: number;
    group_name: string;
    group_delta_boost?: number;
    plots?: any[];
}

interface BuyerBid {
    id: number;
    market_day: string;
    price_per_kg: number;
    updated_by: number;
}

interface ModalProps {
    price?: PriceModel;
    statementId?: number;
    isOpen: boolean;
    onDismiss: (data?: any | null | undefined | number, role?: string) => void;
}

// We save the previous datas for Aliva
var cachedInfo: {
    buyer?: Organisation;
    buyerBid?: BuyerBid;
    groups?: GroupModel[];
    groupSelected?: GroupModel;
    agentOrgs?: Organisation[];
    agentOrgSelected?: Organisation;
} = {};

const ModalNAMContractCreationEdition: React.FC<ModalProps> = ({ price, statementId, isOpen, onDismiss }) => {
    // Get authenticated user from Redux
    const { userId, transactorId, token } = useAppSelector(state => state.auth);

    // Fetch contract if statementId is provided
    const { data: contractData, loading: contractLoading } = useQuery(getContractAgent, {
        skip: !statementId || !isOpen,
        variables: { statementId },
        fetchPolicy: 'network-only'
    });

    const [contract, setContract] = useState<ContractNAM | undefined>(undefined)

    useEffect(() => {
        if (contractData) {
            console.log('contractDatac', contractData)
            const builtContract = ContractNAM.build((contractData as any).contract);
            setContract(builtContract);

            // Populate drop-off data from contract if financial net quantity exists
            if (builtContract.getNetQuantity()) {
                const receiptPhotos = builtContract.receiptPhotos();
                const weightPhoto = receiptPhotos.find(p => p.isWeightReceipt());
                const sortationPhoto = receiptPhotos.find(p => p.isSortationTicket());

                if (weightPhoto && sortationPhoto) {
                    setDropOffData({
                        quantity_in: 0,  // Not needed in readonly mode
                        quantity_out: 0,  // Not needed in readonly mode
                        receipt_file: new File([], ''), // Dummy file for interface compatibility
                        sortation_ticket_file: new File([], ''), // Dummy file for interface compatibility
                        net_quantity: builtContract.getNetQuantity(),
                        receipt_uri: weightPhoto.uri,
                        sortation_ticket_uri: sortationPhoto.uri,
                    });
                }
            }
        } else {
            setContract(undefined);
            setDropOffData(null);
        }
    }, [contractData])

    const isCreation = useMemo(() => {
        return isNullOrUndefined(contract)
    }, [contract])

    // Determine if NE Mobile quick create is available
    // For editing: only if buyer accepts NE Mobile AND contract status is OPEN
    // For creating: only if price accepts NE Mobile
    const acceptsNeMobileDropoff = contract
        ? (contract.buyer?.accepts_ne_mobile_dropoff === true && contract.status === "OPEN")
        : (price?.acceptsNeMobileDropoff ?? false);

    const [agentOrgs, setAgentOrgs] = useState<Organisation[] | undefined>(cachedInfo?.agentOrgs);
    const [agentOrgSelected, setAgentOrgSelected] = useState<Organisation | undefined>(cachedInfo?.agentOrgSelected);
    const [buyer, setBuyer] = useState<Organisation | undefined>(cachedInfo?.buyer);
    const [buyerBid, setBuyerBid] = useState<BuyerBid | undefined>(cachedInfo?.buyerBid);
    const [isBuyerPickerOpen, setIsBuyerPickerOpen] = useState(false);
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [isDropOffModalOpen, setIsDropOffModalOpen] = useState(false);
    const [dropOffData, setDropOffData] = useState<DropOffData | null>(null);
    const [pickupPhotos, setPickupPhotos] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<string>('');
    const [uploading, setUploading] = useState(false)

    // Mutation hooks
    const [createNgpamContract, { loading: ngpamLoading }] = useMutation(NgpamCreateContract);
    const [createNamContract, { loading: namLoading }] = useMutation(NamCreateContract);
    const [updateNamDropoff, { loading: namUpdateLoading }] = useMutation(NamSetDropoff);
    const [updateNgpamDropoff, { loading: ngpamUpdateLoading }] = useMutation(NgpamSetDropoff);

    // Get Apollo Client for direct queries
    const apolloClient = useApolloClient();

    const isLoading = ngpamLoading || namLoading || namUpdateLoading || ngpamUpdateLoading || !isNullOrEmpty(uploadProgress);

    // Pre-populate buyer from contract or price prop if provided
    useEffect(() => {
        if (isOpen) {
            if (contract) {
                // Edit mode: populate from contract
                setBuyer(contract.buyer);

                // Set agent org from contract
                if (contract.agent) {
                    setAgentOrgSelected(contract.agent);
                }

                // TODO: Populate drop-off and pickup photos from contract if available

            } else if (price) {
                // Create mode: populate from price
                setBuyer(price.organisation);

                const bidData = {
                    id: price.id,
                    market_day: '',
                    price_per_kg: price.pricePerUnit,
                    updated_by: 0
                };
                setBuyerBid(bidData);
            }
        }
    }, [price, contract, isOpen, statementId]);

    // TODO: Implement buyer picker functionality

    const openBuyerPicker = () => {
        setIsBuyerPickerOpen(true);
    };

    const handleBuyerPickerDismiss = (data?: any, role?: string) => {
        setIsBuyerPickerOpen(false);

        if (role === 'confirm' && data?.chosen) {
            const selectedBuyer = data.chosen;
            setBuyer(selectedBuyer);
            setBuyerBid(selectedBuyer.bids?.at(0));
        }
    };

    const handleDropOffDismiss = (data?: DropOffData | null, role?: string) => {
        setIsDropOffModalOpen(false);
        if (role === 'confirm' && data) {
            setDropOffData(data);
        }
    };

    const handlePickupPhotoAdd = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
            setPickupPhotos(prev => [...prev, ...newFiles]);
        }
        // Reset input value to allow selecting the same file again
        event.target.value = '';
    };

    const handlePickupPhotoRemove = (index: number) => {
        setPickupPhotos(prev => prev.filter((_, i) => i !== index));
    };


    // Helper function to generate Firebase storage path similar to Android implementation
    const generateReceiptFirebasePhotoPath = (
        type: string,
        buyerOrganizationId?: number,
        party?: string
    ): string => {
        const agentTransactorId = transactorId;

        // Generate date strings
        const now = new Date();
        const dateIso = now.toISOString(); // UTC with milliseconds
        const dateOfYear = now.toISOString().split('T')[0]; // YYYY-MM-DD

        // Generate UUID from date string using uuid v5 (similar to Android's nameUUIDFromBytes)
        // Using a custom namespace (you can use a specific UUID namespace if needed)
        const NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8'; // DNS namespace UUID
        const uuid = uuidv5(dateIso, NAMESPACE);

        const photoUUID = `${dateOfYear}_${type}_${uuid}`;

        // Build path: /agentTransactor/{agentTransactorId}/buyerOrganisation/{buyerOrganizationId}/{photoUUID}.jpg
        const path = `agentTransactor/${agentTransactorId}/buyerOrganisation/${buyerOrganizationId}/${photoUUID}.jpg`;
        // const path = `abc/${photoUUID}.jpg`;
        return path;
    };

    const generatePickupFirebasePhotoPath = (
        type: string,
        buyerOrganizationId?: number,
        party?: string
    ): string => {
        const agentTransactorId = transactorId;

        // Generate date strings
        const now = new Date();
        const dateIso = now.toISOString(); // UTC with milliseconds
        const dateOfYear = now.toISOString().split('T')[0]; // YYYY-MM-DD

        // Generate UUID from date string using uuid v5 (similar to Android's nameUUIDFromBytes)
        // Using a custom namespace (you can use a specific UUID namespace if needed)
        const NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8'; // DNS namespace UUID
        const uuid = uuidv5(dateIso, NAMESPACE);

        const photoUUID = `${dateOfYear}_${type}_${uuid}`;

        // Build path: /agentTransactor/{agentTransactorId}/buyerOrganisation/{buyerOrganizationId}/{photoUUID}.jpg
        const path = `agentTransactor/${agentTransactorId}/buyerOrganisation/${buyerOrganizationId}/${photoUUID}.jpg`;
        // const path = `abc/${photoUUID}.jpg`;
        return path;
    };

    // Helper function to get signed URLs using GraphQL query
    const getSignedUrls = async (files: { filename: string; contentType: string }[]) => {
        const urlPromises = files.map(async (file) => {
            const { data } = await apolloClient.query({
                query: GET_DROPOFF_PHOTO_URL,
                variables: { sub_url: file.filename },
                fetchPolicy: 'no-cache'
            });
            return {
                uploadUrl: (data as any).url,
                downloadUrl: (data as any).url
            };
        });

        return Promise.all(urlPromises);
    };

    // Helper function to upload file to Firebase using signed URL
    const uploadToFirebase = async (signedUrl: string, file: File) => {

        console.log("uplaoding to signed url", signedUrl, file)

        const response = await fetch(signedUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': 'application/octet-stream',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to upload ${file.name}`);
        }
    };

    // TODO: Replace with actual query once GraphQL schema is defined
    const { data: agentOrgData } = useQuery(TransactorCompaniesV2, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        skip: !transactorId,
        variables: {
            filter: {
                transactor_id: { _eq: transactorId },
                _and: [
                    { organisation: { organisation_type: { _neq: "ramp" } } },
                    { organisation: { organisation_type: { _neq: "mill" } } },
                    { organisation: { organisation_type: { _neq: "loading_ramp" } } },
                    { organisation: { organisation_type: { _neq: "handling" } } },
                    { organisation: { organisation_type: { _neq: "drop_off" } } }
                ],
                organisation: { deleted_at: { _is_null: true } }
            }
        }
    });

    useEffect(() => {
        console.log('orgs', agentOrgData, transactorId)
        if ((agentOrgData as any)?.orgs) {
            const orgs = (agentOrgData as any).orgs.map((o: any) => o.organisation);
            setAgentOrgs(orgs);
            setAgentOrgSelected(orgs?.at(0));
        }
    }, [agentOrgData]);

    const getBuyer = () => {
        if (isCreation) {

        } else {
            return contract?.getBuyer()
        }
    }

    const handleCreate = async () => {
        setMutationError(null);
        setUploadProgress('');

        cachedInfo = {
            buyer: buyer,
            buyerBid: buyerBid,
            agentOrgs: agentOrgs,
            agentOrgSelected: agentOrgSelected
        };

        try {
            let uploadedDropOffData = null;
            let uploadedPickupPhotos: string[] = [];

            // Upload drop-off files if present
            if (dropOffData) {
                setUploadProgress('Uploading drop-off photos...');

                // Get signed URLs for drop-off files
                const signedUrls = await getSignedUrls([
                    { filename: generateReceiptFirebasePhotoPath("weight_receipt", buyer?.id, contract?.party), contentType: dropOffData.receipt_file.type },
                    { filename: generateReceiptFirebasePhotoPath("sortation_ticket", buyer?.id, contract?.party), contentType: dropOffData.sortation_ticket_file.type }
                ]);

                console.log('dropoff firelink', signedUrls)

                // Upload files to Firebase
                await uploadToFirebase(signedUrls[0].uploadUrl, dropOffData.receipt_file);
                await uploadToFirebase(signedUrls[1].uploadUrl, dropOffData.sortation_ticket_file);

                uploadedDropOffData = {
                    quantity_in: dropOffData.quantity_in,
                    quantity_out: dropOffData.quantity_out,
                    quantity_deducted: dropOffData.quantity_deducted ?? 0,
                    receipt_uri: `gs://${extractStoragePath(signedUrls[0].downloadUrl)}`,
                    sortation_ticket_uri: `gs://${extractStoragePath(signedUrls[1].downloadUrl)}`,
                };
            }

            // Upload pickup photos if present
            if (pickupPhotos.length > 0) {
                setUploadProgress(`Uploading pickup photos (${pickupPhotos.length})...`);

                const signedUrlRequests = pickupPhotos.map(photo => ({
                    filename: generateReceiptFirebasePhotoPath("sortation_ticket", buyer?.id, contract?.party),
                    contentType: photo.type
                }));

                const signedUrls = await getSignedUrls(signedUrlRequests);

                // Upload all photos
                await Promise.all(
                    pickupPhotos.map((photo, index) =>
                        uploadToFirebase(signedUrls[index].uploadUrl, photo)
                    )
                );

                uploadedPickupPhotos = signedUrls.map((url: any) => `gs://${extractStoragePath(url.downloadUrl)}`);
            }

            setUploadProgress('Creating contract...');

            // Determine mutation type based on group presence in price
            const hasGroup = !!price?.groupId;

            if (hasGroup) {
                // Use ngpam_create_contract mutation (with group)
                const { data } = await createNgpamContract({
                    variables: {
                        input: {
                            price_id: buyerBid?.id,
                            buyer_organisation_id: buyer?.id,
                            group_id: price?.groupId,
                            ...(uploadedDropOffData && {
                                ngpam_dropoff_data: uploadedDropOffData
                            })
                        }
                    }
                });
                console.log('Contract created (with group):', data);
                onDismiss({ contractId: (data as any)?.contract?.contract_id }, 'confirm');
            } else {
                // Use nam_create_contract mutation (with agent)
                const { data } = await createNamContract({
                    variables: {
                        input: {
                            price_id: buyerBid?.id,
                            buyer_organisation_id: buyer?.id,
                            agent_organisation_id: agentOrgSelected?.id,
                            agent_transactor_id: transactorId,
                            ...(uploadedPickupPhotos.length > 0 && {
                                nam_pickup_data: [{
                                    photos: uploadedPickupPhotos.map((url, index) => ({
                                        url: url,
                                        type: pickupPhotos[index].type,
                                    }))
                                }]
                            }),
                            ...(uploadedDropOffData && {
                                nam_dropoff_data: uploadedDropOffData
                            })
                        }
                    }
                });
                console.log('Contract created (with agent):', data);
                onDismiss({ contractId: (data as any)?.contract?.contract_id }, 'confirm');
            }
        } catch (error: any) {
            console.error('Failed to create contract:', error);
            setMutationError(error.message || 'Failed to create contract');
            setUploadProgress('');
        }
    };

    const didDropOffPhotosChanged = () => {
        return dropOffData?.receipt_file.size !== 0 && dropOffData?.sortation_ticket_file.size !== 0
    }

    const handleUpdate = async () => {
        if (!contract?.contract_id) {
            setMutationError('No contract to update');
            return;
        }

        setMutationError(null);
        setUploadProgress('');

        try {
            let uploadedDropOffData = null;

            // Upload drop-off files if present
            if (didDropOffPhotosChanged() && dropOffData) {
                setUploadProgress('Uploading drop-off photos...');

                // Get signed URLs for drop-off files
                const signedUrls = await getSignedUrls([
                    { filename: generateReceiptFirebasePhotoPath("weight_receipt", buyer?.id, contract?.party), contentType: dropOffData.receipt_file.type },
                    // { filename: generateFirebasePhotoPath("weight_receipt", buyer?.id, contract?.party), contentType: dropOffData.receipt_file.type }
                    { filename: generateReceiptFirebasePhotoPath("sortation_ticket", buyer?.id, contract?.party), contentType: dropOffData.sortation_ticket_file.type }
                ]);

                console.log('res signed url', signedUrls)

                // Upload files to Firebase
                await uploadToFirebase(signedUrls[0].uploadUrl, dropOffData.receipt_file);
                await uploadToFirebase(signedUrls[1].uploadUrl, dropOffData.sortation_ticket_file);

                if (contract.isDelivered()) {
                    alert('Contract updated successfully!');
                    onDismiss({ contractId: contract.contract_id }, 'confirm');
                    return
                }

                uploadedDropOffData = {
                    quantity_in: dropOffData.quantity_in,
                    quantity_out: dropOffData.quantity_out,
                    quantity_deducted: dropOffData.quantity_deducted ?? 0,
                    receipt_uri: `gs://${extractStoragePath(signedUrls[0].downloadUrl)}`,
                    sortation_ticket_uri: `gs://${extractStoragePath(signedUrls[1].downloadUrl)}`,
                };
            }

            if (!uploadedDropOffData) {
                setMutationError('Drop-off data is required for update');
                return;
            }

            setUploadProgress('Updating contract...');

            // Determine mutation type based on group presence
            const hasGroup = !!price?.groupId;

            if (hasGroup) {
                // Use ngpam_set_dropoff mutation
                const { data } = await updateNgpamDropoff({
                    variables: {
                        input: {
                            contract_id: contract.contract_id,
                            agent_organisation_id: contract.agent?.id,
                            dropoff_data: uploadedDropOffData
                        }
                    }
                });
                console.log('Contract updated (group):', data);
                alert('Contract updated successfully!');
                onDismiss({ contractId: contract.contract_id }, 'confirm');
            } else {
                // Use nam_set_dropoff mutation
                const { data } = await updateNamDropoff({
                    variables: {
                        input: {
                            contract_id: contract.contract_id,
                            agent_organisation_id: contract.agent?.id,
                            dropoff_data: uploadedDropOffData
                        }
                    }
                });
                console.log('Contract updated (agent):', data);
                alert('Contract updated successfully!');
                onDismiss({ contractId: contract.contract_id }, 'confirm');
            }
        }
        catch (error: any) {
            console.error('Failed to update contract:', error);
            setMutationError(error.message || 'Failed to update contract');
            setUploadProgress('');
        }
    };

    const handleNEMobileQuickCreate = async () => {
        if (!price) {
            alert('No price selected');
            return;
        }

        try {
            // Create contract with minimal data for NE Mobile
            const { data } = await createNamContract({
                variables: {
                    input: {
                        price_id: price.id,
                        buyer_organisation_id: price?.organisation.id,
                        agent_organisation_id: agentOrgSelected?.id,
                        agent_transactor_id: transactorId,
                        nam_pickup_data: null,
                        nam_dropoff_data: null,
                    }
                }
            });

            console.log('NE Mobile contract created:', data);
            alert('NE Mobile contract created successfully!');
            onDismiss({ contractId: (data as any)?.contract?.contract_id }, 'confirm');
        } catch (error: any) {
            console.error('Error creating NE Mobile contract:', error);
            setMutationError(error.message || 'Failed to create NE Mobile contract');
        } finally {
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={() => onDismiss(null, 'cancel')}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {contract ? 'Edit Contract' : 'Create Contract At Mill'}
                    </Typography>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={() => onDismiss(null, 'cancel')}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Error Alert */}
                    {mutationError && (
                        <Alert severity="error" onClose={() => setMutationError(null)}>
                            {mutationError}
                        </Alert>
                    )}

                    {/* Upload Progress */}
                    {uploadProgress && (
                        <Alert severity="info">
                            {uploadProgress}
                        </Alert>
                    )}

                    {/* Buyer Section */}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Buyer
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                            {buyer && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Org ID: {buyer?.id}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <BusinessIcon color="info" />
                                        <Typography variant="body1" fontWeight="bold" color="info.main">
                                            {buyer?.name}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            {/* <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={openBuyerPicker}
                            >
                                Set Buyer
                            </Button> */}

                            {buyerBid !== undefined && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle2">Bid:</Typography>
                                        {buyerBid?.price_per_kg >= 0 ? (
                                            <Chip
                                                label={`${buyerBid?.price_per_kg} Rp`}
                                                color="info"
                                                size="small"
                                            />
                                        ) : (
                                            <Typography variant="body2" color="error">
                                                No Bid for this mill today
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    {/* Agent Organization Section */}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Agent Organization
                        </Typography>
                        <GeneralSelector
                            disabled={!isNullOrUndefined(contract?.contract_id) || contractLoading}
                            data={agentOrgs?.map(o => ({ id: o.id, name: `${o.name} (${o.type})` })) ?? []}
                            placeholder="Select Agent Organization"
                            label="Agent Org"
                            hasTitle={false}
                            initValue={agentOrgSelected?.id}
                            onChange={(v) => setAgentOrgSelected(agentOrgs?.find(o => o.id === v?.id))}
                            clearable={true}
                        />
                    </Box>

                    {/* Drop-Off Section (Optional) */}
                    {!acceptsNeMobileDropoff && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Drop-Off Information (Optional)
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                {dropOffData ? (
                                    <Box>
                                        {dropOffData.net_quantity ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography variant="body2">Net Quantity:</Typography>
                                                <Chip
                                                    label={`${dropOffData.net_quantity} kg`}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </Box>
                                        ) : (
                                            <div hidden={contract?.isDelivered()}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2">Quantity In:</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{dropOffData.quantity_in} kg</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2">Quantity Out:</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{dropOffData.quantity_out} kg</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                    <Typography variant="body2">Quantity Deducted:</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{dropOffData.quantity_deducted} kg</Typography>
                                                </Box>
                                            </div>
                                        )}
                                        {!dropOffData.net_quantity && (
                                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', mb: 2 }}>
                                                <Chip label={`Receipt: ${dropOffData.receipt_file.name}`} size="small" color="success" />
                                                <Chip label={`Ticket: ${dropOffData.sortation_ticket_file.name}`} size="small" color="success" />
                                            </Box>
                                        )}
                                        <Button
                                            disabled={contractLoading}
                                            variant="outlined"
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => setIsDropOffModalOpen(true)}
                                            fullWidth
                                        >
                                            Edit Drop-Off
                                        </Button>
                                    </Box>
                                ) : (
                                    <Button
                                        disabled={contractLoading}
                                        variant="outlined"
                                        size="small"
                                        startIcon={<LocalShippingIcon />}
                                        onClick={() => setIsDropOffModalOpen(true)}
                                        fullWidth
                                    >
                                        Add Drop-Off Information
                                    </Button>
                                )}
                            </Paper>
                        </Box>
                    )}

                    {/* Pickup Photos Section */}
                    {/* {(price?.programPricePerUnit ?? 0) > 0 && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Pickup Photos (Optional)
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handlePickupPhotoAdd}
                                    style={{ display: 'none' }}
                                    id="pickup-photo-input"
                                />
                                <label htmlFor="pickup-photo-input">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<AddPhotoAlternateIcon />}
                                        fullWidth
                                        size="small"
                                    >
                                        Add Pickup Photos
                                    </Button>
                                </label>

                                {pickupPhotos.length > 0 && (
                                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {pickupPhotos.map((photo, index) => (
                                            <Paper key={index} sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                                                <img
                                                    src={URL.createObjectURL(photo)}
                                                    alt={`Pickup ${index + 1}`}
                                                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" noWrap>
                                                        {photo.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(photo.size / 1024).toFixed(2)} KB
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handlePickupPhotoRemove(index)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Paper>
                                        ))}
                                        <Typography variant="caption" color="text.secondary">
                                            {pickupPhotos.length} photo{pickupPhotos.length !== 1 ? 's' : ''} selected
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    )} */}

                    {/* Group Section */}
                    {price?.groupId && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Group
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    Group ID: {price.groupId}
                                </Typography>
                                {price.groupPricePerUnit && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                        <Typography variant="subtitle2">Group Bonus:</Typography>
                                        <Chip
                                            label={`${price.groupPricePerUnit} Rp/kg`}
                                            color="success"
                                            size="small"
                                        />
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => onDismiss(null, 'cancel')}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                {acceptsNeMobileDropoff ? (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleNEMobileQuickCreate}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                    >
                        {isLoading ? 'Creating...' : 'Notify the mill that I am coming'}
                    </Button>
                ) : contract?.contract_id ?
                    <Button
                        variant="contained"
                        onClick={handleUpdate}
                        disabled={
                            isLoading
                        }
                        startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                    >
                        {isLoading ? (uploadProgress || 'Processing...') : 'Confirm'}
                    </Button>
                    : (
                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={
                                isLoading ||
                                !buyerBid ||
                                !buyer ||
                                (!price?.groupId && !agentOrgSelected)
                            }
                            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                        >
                            {isLoading ? (uploadProgress || 'Processing...') : 'Confirm'}
                        </Button>
                    )}
            </DialogActions>

            <ModalPickUpBid
                open={isBuyerPickerOpen}
                type="mill"
                onDismiss={handleBuyerPickerDismiss}
            />

            <ModalDropOff
                isOpen={isDropOffModalOpen}
                onDismiss={handleDropOffDismiss}
                initialData={dropOffData || undefined}
                contract={contract}
            />
        </Dialog>
    );
};

export default ModalNAMContractCreationEdition;